import { isAfter } from 'date-fns';
import isWithinInterval from 'date-fns/isWithinInterval';
import parseISO from 'date-fns/parseISO';
import { IncidentPriorityRating } from '../types/Incident';
import { isDefined, mapArrayToObject } from '../utils';
// Context: sentinelsAPI returns incidents with a buffer zone of 30 days prior to the date filter time range
// to include still active incidents that haven't triggered a new notification
// within the time range of the date filter
// however, it also picks up resolved incidents outside of the time range
// we need this function to filter out those resolved incidents
export const filterResolvedIncidentsOutsideOfTimeRange = (incidents, fromDate, toDate) => {
    return incidents.filter((i) => {
        if (isDefined(i.recoveredAt)) {
            return isWithinInterval(parseISO(i.recoveredAt), {
                start: fromDate,
                end: toDate,
            });
        }
        return true;
    });
};
export const getSubbedIncidentResourceId = (normalizedIncident) => normalizedIncident.equipmentId || normalizedIncident.spaceId || '';
export const normalizeSubscribedIncident = (rawData) => {
    var _a, _b, _c, _d, _e, _f;
    return ({
        createdAt: rawData.createdAt,
        equipmentId: rawData.equipmentId,
        spaceId: rawData.spaceId,
        incidentId: rawData.incidentId,
        incidentNotification: rawData.incidentNotification,
        incidentPriority: rawData.incidentPriority,
        notificationCount: rawData.notificationCount,
        recoveredAt: rawData.recoveredAt,
        resolved: rawData.resolved,
        sentinelId: rawData.sentinelId,
        dismissedAt: rawData.dismissedAt,
        //normalized values
        siteId: (_c = (_b = (_a = rawData.resourcePath) === null || _a === void 0 ? void 0 : _a.sites) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.siteId,
        sensorId: (_f = (_e = (_d = rawData.resourcePath) === null || _d === void 0 ? void 0 : _d.sensors) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.sensorId
    });
};
export const processNormalizedIncidents = (normalizedIncidents, existingIncidentsByResourceId) => {
    const possibleAlerts = [];
    const incidentsByResourceId = Object.assign({}, existingIncidentsByResourceId);
    normalizedIncidents.forEach((normalizedIncident) => {
        const resourceId = getSubbedIncidentResourceId(normalizedIncident);
        const { sentinelId, dismissedAt, resolved, incidentNotification, incidentPriority } = normalizedIncident;
        const isIncidentNotificationsValid = incidentNotification.length > 0;
        const isIncidentPriorityValid = !!incidentPriority;
        const isIncidentValid = !!resourceId && isIncidentNotificationsValid && isIncidentPriorityValid;
        if (isIncidentValid) {
            if (incidentsByResourceId[resourceId] === undefined) {
                incidentsByResourceId[resourceId] = {};
            }
            incidentsByResourceId[resourceId][sentinelId] = normalizedIncident;
            const newAlertIdentifier = {
                resourceId,
                sentinelId,
                lastNotificationAt: getLatestNotificationAt(normalizedIncident),
                incidentPriority: normalizedIncident.incidentPriority
            };
            possibleAlerts.push(newAlertIdentifier);
        }
    });
    return { possibleAlerts, incidentsByResourceId };
};
const getLatestNotificationAt = (incident) => {
    if (incident.incidentNotification.length === 0)
        return '';
    return incident.incidentNotification[incident.incidentNotification.length - 1]
        .at;
};
export const updateAlertIdentifiers = (previousAlerts, possibleAlerts) => {
    if (possibleAlerts.length === 0)
        return previousAlerts;
    // How this key is constructed will determine how the toast alerts are shown.
    // I.e. with a key `${alert.resourceId}-${alert.sentinelId}` you will get
    // multiple toasts per equipment if there are multiple sentinels. Harsh
    // requested we only show one per resource - picking the latest if there are
    // multiple, hence the `${alert.resourceId}`
    const constructAlertKey = (alert) => `${alert.resourceId}`;
    const updatedAlertIdentifiers = mapArrayToObject(previousAlerts.map((i) => (Object.assign(Object.assign({}, i), { key: constructAlertKey(i) }))), 'key');
    possibleAlerts.forEach((possibleAlert) => {
        const possibleAlertKey = constructAlertKey(possibleAlert);
        if (!isDefined(updatedAlertIdentifiers[possibleAlertKey])) {
            updatedAlertIdentifiers[possibleAlertKey] = possibleAlert;
        }
        else {
            const currentAlertPriority = updatedAlertIdentifiers[possibleAlertKey].incidentPriority;
            const possibleAlertPriority = possibleAlert.incidentPriority;
            const currentAlertTimestamp = updatedAlertIdentifiers[possibleAlertKey].lastNotificationAt;
            const possibleAlertTimestamp = possibleAlert.lastNotificationAt;
            // If multiple incidents for same key keep the one that is highest priority. If
            // same keep the newest
            if (IncidentPriorityRating[possibleAlertPriority] >
                IncidentPriorityRating[currentAlertPriority]) {
                updatedAlertIdentifiers[possibleAlertKey] = possibleAlert;
            }
            else if (IncidentPriorityRating[possibleAlertPriority] ===
                IncidentPriorityRating[currentAlertPriority] &&
                isAfter(parseISO(possibleAlertTimestamp), parseISO(currentAlertTimestamp))) {
                updatedAlertIdentifiers[possibleAlertKey] = possibleAlert;
            }
        }
    });
    return Object.values(updatedAlertIdentifiers);
};
